import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminStaffAbsencesApi } from '@api/client';
import { useConfirm } from '@hooks/useConfirm';
import type { StaffAbsence, StaffAbsenceReason } from '@api/types';
import * as Icons from '@components/Icons';

const REASON_LABEL: Record<StaffAbsenceReason, string> = {
  vacation: 'Vacaciones',
  sick:     'Baja por enfermedad',
  other:    'Otro',
};

interface StaffInfo {
  id: string;
  name: string;
  centerName: string;
}

interface AbsencesSectionProps {
  staff: readonly StaffInfo[];
}

export function AbsencesSection({ staff }: AbsencesSectionProps) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<StaffAbsence | 'new' | null>(null);

  const { data, isLoading } = useQuery<StaffAbsence[]>({
    queryKey: ['staff-absences'],
    queryFn: () => adminStaffAbsencesApi.list(),
    staleTime: 30_000,
  });

  const invalidate = async () => {
    // Invalidate both the full list and the per-week query used by HorariosPage grid
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['staff-absences'] }),
      qc.invalidateQueries({ queryKey: ['staff-absences-week'] }),
    ]);
  };

  const createMutation = useMutation({
    mutationFn: adminStaffAbsencesApi.create,
    onSuccess: () => { void invalidate(); setEditing(null); },
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof adminStaffAbsencesApi.update>[1] }) =>
      adminStaffAbsencesApi.update(id, patch),
    onSuccess: () => { void invalidate(); setEditing(null); },
  });
  const deleteMutation = useMutation({
    mutationFn: adminStaffAbsencesApi.delete,
    onSuccess: () => { void invalidate(); },
  });

  const staffById = useMemo(() => {
    const m = new Map<string, StaffInfo>();
    for (const s of staff) m.set(s.id, s);
    return m;
  }, [staff]);

  const sorted = useMemo(
    () => [...(data ?? [])].sort((a, b) => a.startDate.localeCompare(b.startDate)),
    [data],
  );

  return (
    <div className="card" style={{ marginTop: 24 }}>
      <div className="card-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span className="card-title">Ausencias (vacaciones / bajas)</span>
        <button className="btn" data-size="sm" onClick={() => { setEditing('new'); }}>
          <Icons.Plus size={12} /> Nueva ausencia
        </button>
      </div>

      <div style={{ padding: '8px 18px 16px' }}>
        <p style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 0 }}>
          Bloquea al profesional para todo el rango de fechas. Las ausencias se aplican siempre
          en el widget, esté o no activo el toggle "Usar horarios para disponibilidad".
        </p>

        {isLoading ? (
          <div style={{ padding: 16 }}><span className="spinner" /></div>
        ) : sorted.length === 0 ? (
          <div style={{ padding: 16, color: 'var(--fg-soft)', fontSize: 13 }}>
            Sin ausencias registradas.
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Profesional</th>
                <th>Desde</th>
                <th>Hasta</th>
                <th>Motivo</th>
                <th>Notas</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sorted.map((a) => {
                const s = staffById.get(a.staffId);
                return (
                  <tr key={a.id}>
                    <td>
                      <div className="cell-bold">{s?.name ?? a.staffId.slice(0, 8)}</div>
                      <div className="cell-muted" style={{ fontSize: 11 }}>{s?.centerName ?? '—'}</div>
                    </td>
                    <td className="mono">{a.startDate}</td>
                    <td className="mono">{a.endDate}</td>
                    <td>{REASON_LABEL[a.reason]}</td>
                    <td style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
                      {a.notes ?? '—'}
                    </td>
                    <td className="cell-right">
                      <button
                        className="icon-btn"
                        data-variant="ghost"
                        onClick={() => { setEditing(a); }}
                        aria-label="Editar"
                      >
                        <Icons.Edit size={13} />
                      </button>
                      <button
                        className="icon-btn"
                        data-variant="ghost"
                        onClick={() => {
                          void (async () => {
                            const ok = await confirm({
                              title: 'Eliminar ausencia',
                              message: `¿Eliminar ausencia de ${s?.name ?? 'este profesional'}?`,
                              tone: 'danger',
                              confirmLabel: 'Eliminar',
                            });
                            if (ok) deleteMutation.mutate(a.id);
                          })();
                        }}
                        aria-label="Eliminar"
                      >
                        <Icons.Trash size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing !== null && (
        <AbsenceEditor
          absence={editing === 'new' ? null : editing}
          staff={staff}
          isSaving={createMutation.isPending || updateMutation.isPending}
          onClose={() => { setEditing(null); }}
          onSave={(payload) => {
            if (editing === 'new') createMutation.mutate(payload);
            else updateMutation.mutate({ id: editing.id, patch: payload });
          }}
        />
      )}
    </div>
  );
}

function todayIso(): string {
  const d = new Date();
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface AbsenceEditorProps {
  absence: StaffAbsence | null;
  staff: readonly StaffInfo[];
  isSaving: boolean;
  onClose: () => void;
  onSave: (payload: {
    staffId: string;
    startDate: string;
    endDate: string;
    reason: StaffAbsenceReason;
    notes?: string;
  }) => void;
}

function AbsenceEditor({ absence, staff, isSaving, onClose, onSave }: AbsenceEditorProps) {
  const fallbackId = staff[0]?.id ?? '';
  const [staffId, setStaffId] = useState<string>(absence?.staffId ?? fallbackId);
  const [startDate, setStartDate] = useState(absence?.startDate ?? todayIso());
  const [endDate, setEndDate] = useState(absence?.endDate ?? todayIso());
  const [reason, setReason] = useState<StaffAbsenceReason>(absence?.reason ?? 'vacation');
  const [notes, setNotes] = useState(absence?.notes ?? '');
  const [error, setError] = useState('');

  function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setError('');
    if (!staffId) { setError('Selecciona un profesional'); return; }
    if (startDate > endDate) { setError('La fecha de inicio debe ser anterior o igual a la fecha de fin'); return; }
    onSave({
      staffId,
      startDate,
      endDate,
      reason,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    });
  }

  return (
    <div className="modal-scrim" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} role="dialog" aria-modal="true">
      <form className="modal" style={{ width: 'min(480px, 100%)' }} onSubmit={handleSubmit}>
        <div className="modal-head">
          <h2 className="modal-title">{absence ? 'Editar ausencia' : 'Nueva ausencia'}</h2>
          <button type="button" className="icon-btn" data-variant="ghost" onClick={onClose}>
            <Icons.X size={14} />
          </button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!absence && (
            <div className="field">
              <label className="field-label">Profesional *</label>
              <select
                className="select"
                value={staffId}
                onChange={(e) => { setStaffId(e.target.value); }}
                required
              >
                {staff.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} — {s.centerName}</option>
                ))}
              </select>
            </div>
          )}
          <div className="field-row" style={{ display: 'flex', gap: 12 }}>
            <div className="field" style={{ flex: 1 }}>
              <label className="field-label">Desde *</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); }}
                required
              />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label className="field-label">Hasta *</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); }}
                required
              />
            </div>
          </div>
          <div className="field">
            <label className="field-label">Motivo *</label>
            <select
              className="select"
              value={reason}
              onChange={(e) => { setReason(e.target.value as StaffAbsenceReason); }}
            >
              <option value="vacation">Vacaciones</option>
              <option value="sick">Baja por enfermedad</option>
              <option value="other">Otro</option>
            </select>
          </div>
          <div className="field">
            <label className="field-label">Notas (opcional)</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => { setNotes(e.target.value); }}
              maxLength={500}
              placeholder="Ej. cubre Pedro hasta el 10/06"
            />
          </div>
          {error && <div style={{ color: 'var(--danger)', fontSize: 12.5 }}>{error}</div>}
        </div>
        <div className="modal-foot">
          <button type="button" className="btn" onClick={onClose} disabled={isSaving}>
            Cancelar
          </button>
          <button type="submit" className="btn" data-variant="primary" disabled={isSaving || !staffId}>
            {isSaving ? 'Guardando…' : (absence ? 'Guardar cambios' : 'Crear ausencia')}
          </button>
        </div>
      </form>
    </div>
  );
}
